import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowSquareOut, Gauge, Lightning, PencilSimple, Plus } from '@phosphor-icons/react';
import { ImmoButton, KpiReihe, KpiTile, LeerZustand, ToastPill, useToastPill } from '../../../../../components/immobilien/ui';
import { supabase } from '../../../../../lib/supabase/client';
import { deDatum } from '../../../../../lib/immobilien/objektDetail';
import { useNutzerId } from '../../../../../lib/immobilien/useNutzer';
import type { ZaehlerZeile } from '../../../../../lib/immobilien/useObjektDetail';
import type { SucheOption } from '../../../../../components/immobilien/ui';
import { AblesungEditor, ZaehlerEditor } from './ZaehlerEditoren';
import { useObjektSeite } from '../ObjektSeitenKontext';
import { typInfo } from '../../../../../lib/zaehler/typen';

interface LetzterStand { wert: number; datum: string }

/** Panel „Zähler“: alle Zähler des Objekts mit letztem Stand; Stand erfassen, Zähler hinzufügen/bearbeiten. */
export function ZaehlerPanel() {
  const { daten, neuLaden, uebersicht } = useObjektSeite();
  return <ZaehlerVerwaltung zaehler={daten.zaehler} objekt={daten.objekt} einheiten={uebersicht.map(e => e.bezeichnung)} onGeaendert={() => neuLaden('zaehler')} />;
}

/** Zählerliste mit letztem Stand und den bestehenden Formularen – für Objekt und Einheit. */
export function ZaehlerVerwaltung({ zaehler, objekt, onGeaendert, einheiten = [], vorbelegung, leerText = 'Lege Strom-, Gas-, Wasser- oder Wärmezähler an, um Stände zu erfassen.' }: {
  zaehler: readonly ZaehlerZeile[];
  objekt: { id: string; bezeichnung?: string | null; objektnummer?: string | null; strasse?: string | null; hausnummer?: string | null };
  onGeaendert: () => void;
  leerText?: string;
  /** Bezeichnungen der Einheiten (Vorschläge im Feld „Einheit / Wohnung“) */
  einheiten?: readonly string[];
  /** Neuer Zähler von der Einheit-Seite: Einheit und Mieter vorbelegt */
  vorbelegung?: { einheitBezeichnung?: string; mieter?: SucheOption | null };
}) {
  const navigate = useNavigate();
  const nutzerId = useNutzerId();
  const [toast, zeige] = useToastPill();
  const [staende, setStaende] = useState<Record<string, LetzterStand>>({});
  const [runde, setRunde] = useState(0);
  const [formular, setFormular] = useState<{ art: 'neu' } | { art: 'bearbeiten'; z: ZaehlerZeile } | { art: 'ablesung'; z: ZaehlerZeile } | null>(null);

  const aktiv = useMemo(() => zaehler.filter(z => z.aktiv !== false), [zaehler]);
  const inaktiv = zaehler.length - aktiv.length;

  useEffect(() => {
    const ids = zaehler.map(z => z.id);
    if (ids.length === 0) { setStaende({}); return; }
    let laeuft = true;
    supabase.from('ablesungen').select('zaehler_id, wert, datum').in('zaehler_id', ids).order('datum', { ascending: false })
      .then(({ data }) => {
        if (!laeuft) return;
        const m: Record<string, LetzterStand> = {};
        for (const a of (data ?? []) as { zaehler_id: string; wert: number; datum: string }[]) if (!m[a.zaehler_id]) m[a.zaehler_id] = { wert: a.wert, datum: a.datum };
        setStaende(m);
      });
    return () => { laeuft = false; };
  }, [zaehler, runde]);

  const gespeichert = (meldung: string) => { setFormular(null); zeige(meldung); onGeaendert(); setRunde(r => r + 1); };
  const nachTyp = useMemo(() => {
    const m = new Map<string, number>();
    for (const z of aktiv) m.set(typInfo(z.typ).label, (m.get(typInfo(z.typ).label) ?? 0) + 1);
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  }, [aktiv]);
  const ohneStand = aktiv.filter(z => !staende[z.id]).length;

  return (
    <>
      {toast && <ToastPill text={toast.text} fehler={toast.fehler} />}
      <div className="flex items-center justify-between flex-wrap" style={{ gap: 10 }}>
        <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>Zähler</span>
        <div className="flex flex-wrap" style={{ gap: 8 }}>
          <ImmoButton groesse={40} icon={ArrowSquareOut} onClick={() => navigate('/admin/zaehler')}>Zählerbereich</ImmoButton>
          <ImmoButton art="akzent" groesse={40} icon={Plus} onClick={() => setFormular({ art: 'neu' })}>Zähler hinzufügen</ImmoButton>
        </div>
      </div>

      {formular?.art === 'neu' && (
        <ZaehlerEditor objekt={objekt} zaehlerId={null} einheiten={einheiten} vorbelegung={vorbelegung} onAbbrechen={() => setFormular(null)} onGespeichert={gespeichert} />
      )}
      {formular?.art === 'bearbeiten' && (
        <ZaehlerEditor key={formular.z.id} objekt={objekt} zaehlerId={formular.z.id} einheiten={einheiten} onAbbrechen={() => setFormular(null)} onGespeichert={gespeichert} />
      )}
      {formular?.art === 'ablesung' && (
        <AblesungEditor key={formular.z.id} zaehler={formular.z} nutzerId={nutzerId} onAbbrechen={() => setFormular(null)} onGespeichert={gespeichert} />
      )}

      {zaehler.length === 0 ? (
        <LeerZustand icon={Gauge} titel="Noch keine Zähler" text={leerText}>
          <ImmoButton art="akzent" icon={Plus} onClick={() => setFormular({ art: 'neu' })}>Zähler hinzufügen</ImmoButton>
        </LeerZustand>
      ) : (
        <>
          <KpiReihe>
            <KpiTile label="Aktive Zähler" wert={aktiv.length} sub={inaktiv ? `${inaktiv} inaktiv` : 'alle aktiv'} icon={Gauge} ton="acc" />
            <KpiTile label="Nach Art" wert={nachTyp[0] ? `${nachTyp[0][1]} × ${nachTyp[0][0]}` : '–'} sub={nachTyp.slice(1).map(([l, n]) => `${n} ${l}`).join(' · ') || '–'} icon={Lightning} />
            <KpiTile label="Ohne Ablesung" wert={ohneStand} sub={ohneStand ? 'Stand noch nie erfasst' : 'alle abgelesen'} icon={PencilSimple} ton={ohneStand ? 'warn' : 'ok'} wertFarbig={ohneStand > 0} />
          </KpiReihe>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 10 }}>
            {zaehler.map(z => {
              const info = typInfo(z.typ);
              const IconKomp = info.icon;
              const stand = staende[z.id];
              return (
                <div key={z.id} className="immo-innen flex flex-col" style={{ borderRadius: 20, padding: 14, gap: 12, opacity: z.aktiv === false ? 0.55 : 1 }}>
                  <div className="flex items-center" style={{ gap: 12 }}>
                    <span className="flex items-center justify-center shrink-0" style={{ width: 42, height: 42, borderRadius: 14, background: info.flaeche }}><IconKomp weight="fill" size={20} color={info.farbe} /></span>
                    <button type="button" onClick={() => navigate(`/admin/zaehler/${encodeURIComponent(z.id)}`)} className="flex flex-col flex-1 min-w-0 text-left cursor-pointer"
                      style={{ border: 'none', background: 'transparent', padding: 0, color: 'var(--ink)' }} title="Im Zählerbereich öffnen">
                      <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{info.label}{z.aktiv === false ? ' · inaktiv' : ''}</span>
                      <span className="truncate immo-zahl" style={{ fontSize: 12, color: 'var(--ink3)' }}>{z.zaehler_nummer || 'ohne Nummer'}{z.einheit_bezeichnung ? ` · ${z.einheit_bezeichnung}` : z.standort ? ` · ${z.standort}` : ''}</span>
                    </button>
                  </div>
                  <div className="flex items-end justify-between" style={{ gap: 8 }}>
                    <div className="flex flex-col">
                      <span style={{ fontSize: 11, color: 'var(--ink3)' }}>{stand ? `Abgelesen ${deDatum(stand.datum)}` : 'Noch kein Stand'}</span>
                      <span className="immo-zahl" style={{ fontSize: 20, fontWeight: 800 }}>{stand ? `${Number(stand.wert).toLocaleString('de-DE')} ${z.einheit ?? ''}` : '–'}</span>
                    </div>
                    <div className="flex" style={{ gap: 6 }}>
                      <ImmoButton groesse={32} icon={PencilSimple} aria-label={`Zähler ${z.zaehler_nummer ?? ''} bearbeiten`} onClick={() => setFormular({ art: 'bearbeiten', z })} />
                      {z.aktiv !== false && <ImmoButton art="akzent" groesse={32} icon={Plus} onClick={() => setFormular({ art: 'ablesung', z })}>Stand</ImmoButton>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

    </>
  );
}
